import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { toTitleCase } from '../lib/format'
import { reachBadge } from '../lib/reach'
import { Shell } from '../components/Shell'
import { Player, Spinner, isVideo } from '../components/ui'
import ClipUpload from '../components/ClipUpload'
import FollowButton from '../components/FollowButton'
import Encourage from '../components/Encourage'
import { chipText } from '../lib/encourage'
import ReportSong from '../components/ReportSong'

/**
 * Where a sixty-second hook opens out into the whole song.
 *
 * The Collective is hooks and only hooks — scrolling never turns into
 * listening by accident. This is the deliberate step out of the feed: the full
 * recording, the words, the scripture, who else has covered it, and the way to
 * record your own take.
 *
 * Reachability is entirely an RLS question. If can_reach_song() says no, the
 * select returns nothing and this renders "not open to you" — no client-side
 * rule decides who stands here.
 */
export default function SongRoom() {
  const { id } = useParams()
  const { user } = useSession()
  const nav = useNavigate()

  const [song, setSong] = useState(null)
  const [clips, setClips] = useState([])
  // What this song has been told, if it is yours. Null until five responses --
  // the view withholds it, not the screen.
  const [summary, setSummary] = useState(null)
  const [saidToMe, setSaidToMe] = useState([])
  const [rated, setRated] = useState(false)
  const [src, setSrc] = useState(null)
  // Signed URLs for clips, by clip id. Separate from `src`, which is the whole
  // recording: a song can have a hook and a full take and they are different
  // objects with different windows.
  const [clipSrc, setClipSrc] = useState({})
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const [{ data: s }, { data: cl }] = await Promise.all([
      supabase.from('songs')
        .select(`id, title, reach, finished_at, scripture_refs, allow_covers,
                 songwriter_id, r2_object_key, media_mime, media_filename,
                 duration_seconds, theme_tags, scripture_text, scripture_translation, key,
                 writer:songwriter_id(first_name, last_name)`)
        .eq('id', id).maybeSingle(),
      supabase.from('song_clips')
        .select('id, kind, owner_id, duration_seconds, created_at, owner:owner_id(first_name, last_name)')
        .eq('song_id', id).is('removed_at', null)
        .order('created_at', { ascending: false }),
    ])
    setSong(s ?? null)
    setClips(cl ?? [])

    if (s?.songwriter_id === user.id) {
      const [{ data: sum }, { data: said }] = await Promise.all([
        supabase.from('song_encouragement_summary')
          .select('*').eq('song_id', id).maybeSingle(),
        supabase.from('song_rating_comments')
          .select('chip_key, song_ratings!inner(song_id)')
          .eq('song_ratings.song_id', id),
      ])
      setSummary(sum ?? null)
      setSaidToMe(said ?? [])
    } else {
      const { data: mine } = await supabase.from('song_ratings')
        .select('id').eq('song_id', id).eq('user_id', user.id).maybeSingle()
      setRated(!!mine)
    }
    setReady(true)
  }, [id, user.id])

  useEffect(() => { load() }, [load])

  // The full recording, not the hook. Resolved only when someone asks for it.
  const hear = async () => {
    if (src) return
    const { data, error } = await supabase.functions
      .invoke('r2-playback-url', { body: { song_id: id } })
    if (error) return setErr(readableError(error))
    setSrc(data?.url ?? null)
  }

  /* On your own song the "commitment" gate is pointless — you wrote it, and
     this is now the screen you land on from your profile. Resolve the URL as
     soon as it loads so the page is a player, not a button. */
  useEffect(() => {
    if (song?.songwriter_id === user.id && song.r2_object_key) hear()
    // hear() is idempotent and guards on `src`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song?.id])

  /** Sixty seconds, signed on demand — same as the feed does it. */
  const hearClip = async (clip) => {
    if (clipSrc[clip.id]) return
    const { data } = await supabase.functions
      .invoke('r2-playback-url', { body: { clip_id: clip.id } })
    if (data?.url) setClipSrc((m2) => ({ ...m2, [clip.id]: data.url }))
  }

  if (!ready) return <Shell back="/collective" title="The collective"><Spinner /></Shell>

  if (!song) {
    return (
      <Shell back="/collective" title="The collective" tabs>
        <h2 className="q">This song isn't open to you.</h2>
        <p className="sub">
          The writer may have pulled it back in, or it never reached this far.
        </p>
      </Shell>
    )
  }

  const name = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ')
  const mine = song.songwriter_id === user.id
  const covers = clips.filter((c) => c.kind === 'cover')
  /**
   * The hook, which until now was fetched here and never rendered.
   *
   * That left a writer with nowhere at all to watch their own Collective clip:
   * the feed hides your own work from you by design, and this screen dropped
   * every clip that wasn't a cover on the floor. You could upload one and never
   * see it again.
   */
  const original = clips.find((c) => c.kind === 'original')
  const myCover = covers.find((c) => c.owner_id === user.id)
  const canCover = song.allow_covers && !mine

  return (
    <Shell back={mine ? '/me' : '/collective'}
           title={mine ? 'Your songs' : 'The collective'} tabs>
      <div className="eyebrow">{mine ? 'Your song' : 'From the collective'}</div>
      <h2 className="q">{toTitleCase(song.title) || 'Untitled'}</h2>
      {/* The name is a door. Everywhere a writer is credited, you can get to
          the rest of what they have put out. */}
      <p className="sub" style={{ marginBottom: 6, display: 'flex',
                                 alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button className="linkish" onClick={() => nav(`/writer/${song.songwriter_id}`)}>
          {name(song.writer) || 'A writer'}
        </button>
        <FollowButton writerId={song.songwriter_id} />
      </p>
      <span className={`reachpip r-${song.reach}`}>{reachBadge(song.reach)}</span>

      {song.theme_tags?.length > 0 && (
        <div className="chips" style={{ marginTop: 16 }}>
          {song.theme_tags.map((t) => <span key={t} className="chip">{t}</span>)}
        </div>
      )}

      {/* Scripture is shown in full where there is a passage, because the
          Collective's whole premise is songs that come from somewhere. */}
      {song.scripture_refs?.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <label className="label">{song.scripture_refs.join(' · ')}</label>
          {song.scripture_text && (
            <p className="small">
              {song.scripture_text}
              {song.scripture_translation ? ` — ${song.scripture_translation}` : ''}
            </p>
          )}
        </div>
      )}

      {original && (
        <div style={{ marginTop: 22 }}>
          <label className="label">{mine ? 'Your hook' : 'The hook'}</label>
          <p className="small" style={{ marginTop: -4, marginBottom: 10 }}>
            {mine
              ? 'This is what other writers see when they scroll the Collective.'
              : `${original.duration_seconds || 60} seconds the writer chose.`}
          </p>
          {clipSrc[original.id] ? (
            <Player src={clipSrc[original.id]} mime="video/mp4"
                    duration={original.duration_seconds} />
          ) : (
            <button className="btn btn-quiet" onClick={() => hearClip(original)}>
              Play the hook
              {original.duration_seconds ? ` · ${original.duration_seconds}s` : ''}
            </button>
          )}
        </div>
      )}

      {/* The whole song. Deliberately behind a tap: the hook was the pitch,
          this is the commitment. */}
      <div style={{ marginTop: 22 }}>
        <label className="label">The whole song</label>
        {song.r2_object_key ? (
          src ? (
            <Player src={src} mime={song.media_mime}
                    duration={song.duration_seconds} filename={song.media_filename} />
          ) : (
            <button className="btn btn-quiet" onClick={hear}>
              {isVideo(song.media_mime) ? 'Watch it all' : 'Hear it all'}
              {song.duration_seconds ? ` · ${Math.round(song.duration_seconds / 60)} min` : ''}
            </button>
          )
        ) : (
          <p className="small">The writer hasn't put a recording up — words only so far.</p>
        )}
      </div>

      {covers.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <label className="label">
            {covers.length === 1 ? 'One cover' : `${covers.length} covers`}
          </label>
          <div className="songgroup">
            {covers.map((c) => (
              <div key={c.id}>
              <div className="songrow">
                <span className="songmeta">
                  <b>{name(c.owner) || 'A writer'}</b>
                  <small>{c.duration_seconds ? `${c.duration_seconds}s` : 'A take'}</small>
                </span>
                {c.owner_id === user.id && <span className="pill">Yours</span>}
                <button className="btn-plain" onClick={() => hearClip(c)}>Play</button>
              </div>
              {clipSrc[c.id] && (
                <Player src={clipSrc[c.id]} mime="video/mp4"
                        duration={c.duration_seconds} />
              )}
              </div>
            ))}
          </div>
        </div>
      )}

      {canCover && (
        <div className="reachsub" style={{ marginTop: 26 }}>
          <ClipUpload song={song} kind="cover" clip={myCover}
                      onChange={() => load()} />
        </div>
      )}

      {!song.allow_covers && !mine && (
        <p className="small" style={{ marginTop: 26 }}>
          This writer hasn't opened this song for covers.
        </p>
      )}

      {/* Giving encouragement: only on someone else's song, only once. */}
      {!mine && (
        <div className="reachsub" style={{ marginTop: 26 }}>
          <label className="label">Encourage this writer</label>
          {rated ? (
            <p className="small">
              You've already told them what you thought. Thanks — that's the
              part that keeps people writing.
            </p>
          ) : (
            <Encourage song={song} onSent={() => setRated(true)} />
          )}
        </div>
      )}

      {/* Receiving it: your own song, and only once enough people have spoken
          for the words to mean anything. */}
      {mine && (
        <div style={{ marginTop: 26 }}>
          <label className="label">What people said</label>
          {summary ? (
            <>
              <div className="songgroup">
                {[['Overall', summary.tier_overall], ['Lyrics', summary.tier_lyrics],
                  ['Theology', summary.tier_theology], ['Musicality / fit', summary.tier_fourth]]
                  .filter(([, t]) => t)
                  .map(([label, tier]) => (
                    <div key={label} className="songrow">
                      <span className="songmeta"><b>{label}</b></span>
                      <span className="pill">{tier}</span>
                    </div>
                  ))}
              </div>
              <p className="small" style={{ marginTop: 8 }}>
                {summary.responses} {summary.responses === 1 ? 'person has' : 'people have'} listened
                {summary.pastor_responses > 0
                  ? ` · ${summary.pastor_responses} from church staff` : ''}.
              </p>
              {saidToMe.length > 0 && (
                <ul className="enc-said">
                  {saidToMe.map((c, i) => {
                    const text = chipText(c.chip_key)
                    return text ? <li key={i}>{text}</li> : null
                  })}
                </ul>
              )}
            </>
          ) : (
            <p className="small">
              Nothing to show yet. Once five people have responded you'll see
              what they said — until then it would be one person's mood with a
              decimal point on it.
            </p>
          )}
        </div>
      )}

      {/* The controls a song used to carry inline on the profile list. The
          card comes here now, so this is where they live -- and /sharing was
          never a route, it fell through to the wizard's :step. */}
      {mine && (
        <div className="rows" style={{ marginTop: 26 }}>
          <button className="action" onClick={() => nav(`/songs/${song.id}/share`)}>
            <span>Who hears it<small>{reachBadge(song.reach)}</small></span>
            <span className="ar">›</span>
          </button>
          <button className="action" onClick={() => nav(`/songs/${song.id}/takes`)}>
            <span>Takes<small>Every version you've recorded</small></span>
            <span className="ar">›</span>
          </button>
          <button className="action" onClick={() => nav(`/songs/${song.id}/done`)}>
            <span>Paperwork<small>Signatures, splits, declarations</small></span>
            <span className="ar">›</span>
          </button>
        </div>
      )}

      {!mine && (
        <div style={{ marginTop: 20 }}>
          <ReportSong songId={song.id} title={song.title} />
        </div>
      )}

      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
